import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Row, Col, Table } from 'react-bootstrap'
import { OnityPolicyData } from '../../auth/core/_models'
import { useAuth } from '../../auth'
import { KTSVG } from '../../../../_metronic/helpers'

// IST Date helpers
const formatDateIST = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    const dateOnly = dateString.split('T')[0]
    const parts = dateOnly.split('-')
    if (parts.length >= 3) {
      const year = parts[0]
      const month = parts[1]
      const day = parts[2]
      return `${day}/${month}/${year}`
    }
    return dateString
  } catch {
    return ''
  }
}

const toISTInputFormat = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    const dateOnly = dateString.split('T')[0]
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      return dateOnly
    }
    const parts = dateOnly.split('-')
    if (parts.length >= 3) {
      return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`
    }
    return ''
  } catch {
    return ''
  }
}

type Props = {
  policyData: OnityPolicyData
  policyEffectiveDate?: string
  policyExpiryDate?: string
  policyCrmDbName?: string
  onSuccess: () => void
  preloadedMembers?: EnrolledMember[]
  initialDependents?: any[]
}

interface EnrolledMember {
  Id: string
  EmployeeNumber: string
  Name: string
  Dob: string
  Relation: string
  Gender: string
  Premium: number
  Grade: string
  CrmClientId: string
  PremiumOpted: boolean | null
  AdditionalPremium: number | null
  LivesCharged: number | null
  GradeLower: string | null
  TopupName: string | null
  TopupSumInsured: number | null
  TopupBasePremium: number | null
  TopupGst: number | null
  TopupTotalPremium: number | null
  BasePremium: number | null
  BaseGst: number | null
  BaseTotalPremium: number | null
  TotalPremiumPayable: number | null
  TotalGst: number | null
  SumInsured: number | null
  BaseSumInsured: number | null
  CreatedAt: string
}

type Dependent = {
  id: number
  name: string
  dob: string
  relation: string
  gender: string
}

type FlexiPlan = {
  name: string
  sumInsured: number
  premium: number
}

// Allowed relations: Parents and Parents-in-law
const RELATIONS = ['Parent', 'ParentInLaw']

const GENDERS = ['Male', 'Female', 'Other']

const GST_RATE = 0.18

// Sum Insured options for parental policy
const SUM_INSURED_OPTIONS = [
  { label: '5 Lakhs', value: 500000 },
  { label: '8 Lakhs', value: 800000 },
]

// Premium lookup: sumInsured -> { single, double }
// Premium is EXCLUDING GST@18%
const DEFAULT_PREMIUM_TABLE: Record<number, { single: number; double: number }> = {
  500000: { single: 36390, double: 63380 },
  800000: { single: 53486, double: 82750 },
}

// Client-specific premium tables
const CLIENT_PREMIUM_TABLES: Record<string, Record<number, { single: number; double: number }>> = {
  'FIB0000867C': {
    500000: { single: 36951, double: 73000 },
    800000: { single: 51856, double: 100585 },
  },
  'FIB0000862C': {
    500000: { single: 39300, double: 77918 },
    800000: { single: 59230, double: 109385 },
  },
}

// Flexi Benefit Plans for Parental
const FLEXI_PLANS: FlexiPlan[] = [
  { name: 'Bronze', sumInsured: 300000, premium: 9300 },
  { name: 'Silver', sumInsured: 500000, premium: 15300 },
  { name: 'Gold', sumInsured: 700000, premium: 32500 },
  { name: 'Platinum', sumInsured: 1000000, premium: 50100 },
  { name: 'Platinum++', sumInsured: 1500000, premium: 70000 },
]

// Coverage comparison data for parental flexi
const COVERAGE_DATA = [
  {
    label: 'Sum Insured',
    values: ['INR 3,00,000+', 'INR 5,00,000+', 'INR 7,00,000+', 'INR 10,00,000+', 'INR 15,00,000+'],
  },
  {
    label: 'Room Rent',
    values: ['NA', 'INR 2,000+', 'INR 3,000+', 'As per base policy Sum Insured limit', 'As per base policy Sum Insured limit'],
  },
  {
    label: 'Cataract',
    values: ['INR 1,000+', 'INR 3,000+', 'INR 7,000+', 'INR 10,000+', 'INR 15,000+'],
  },
  {
    label: 'ARMD (Age Related Macular Degeneration) - New Cover*',
    values: ['INR 10,000', 'INR 40,000', 'INR 50,000', 'INR 60,000', 'INR 70,000'],
  },
  {
    label: 'Nursing Care at Home',
    values: ['NA', 'NA', 'INR 5,000', 'INR 5,000', 'INR 5,000'],
  },
  {
    label: 'Grand Parents Cover',
    values: ['NA', 'NA', 'NA', 'Restricted to 1 Grand Parent', 'Restricted to 1 Grand Parent'],
  },
  {
    label: 'OPD - Parents',
    values: ['OPD - Not Applicable for Parents', 'OPD - Not Applicable for Parents', 'OPD - Not Applicable for Parents', 'OPD - Not Applicable for Parents', 'OPD - Not Applicable for Parents'],
  },
]

const PLAN_NAMES = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Platinum++']
const PLAN_PREMIUMS = ['₹9,300', '₹15,300', '₹32,500', '₹50,100', '₹70,000']

export const AcsGlobalParentalEnrollmentForm: React.FC<Props> = ({
  policyData,
  policyEffectiveDate,
  policyExpiryDate,
  policyCrmDbName,
  onSuccess,
  preloadedMembers,
  initialDependents,
}) => {
  // Select client-specific premium table or fallback to default
  const PREMIUM_TABLE = (policyCrmDbName && CLIENT_PREMIUM_TABLES[policyCrmDbName]) || DEFAULT_PREMIUM_TABLE

  // Clients without flexi option
  const NO_FLEXI_CLIENTS: string[] = ['FIB0000862C']
  const flexiEnabled = !NO_FLEXI_CLIENTS.includes(policyCrmDbName || '')

  // Clients with 0% GST
  const ZERO_GST_CLIENTS: string[] = ['FIB0000866C', 'FIB0000867C']
  const gstRate = ZERO_GST_CLIENTS.includes(policyCrmDbName || '') ? 0 : GST_RATE

  // ─── Pro-rata helpers ───────────────────────────────────────────────
  const parseDateOnlyToUtcMs = (dateString: string | null | undefined): number | null => {
    if (!dateString) return null
    const dateOnly = dateString.split('T')[0]?.trim()
    if (!dateOnly) return null
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      const [year, month, day] = dateOnly.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateOnly)) {
      const [day, month, year] = dateOnly.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateOnly)) {
      const [day, month, year] = dateOnly.split('/').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    const ymd = toISTInputFormat(dateOnly)
    if (ymd && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      const [year, month, day] = ymd.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    return null
  }

  const daysBetweenInclusive = (fromMsUtc: number, toMsUtc: number): number => {
    const MS_PER_DAY = 24 * 60 * 60 * 1000
    if (toMsUtc < fromMsUtc) return 0
    return Math.floor((toMsUtc - fromMsUtc) / MS_PER_DAY) + 1
  }

  const getProrationFactor = (): number => {
    // Resolve DOJ: prefer JoiningDate from initialDependents (API response), fallback to policyData.DOJ
    let dojRaw: any = undefined
    if (Array.isArray(initialDependents) && initialDependents.length > 0) {
      for (let i = 0; i < initialDependents.length; i++) {
        const row: any = initialDependents[i]
        if (row?.JoiningDate != null) { dojRaw = row.JoiningDate; break }
        if (row?.joiningdate != null) { dojRaw = row.joiningdate; break }
        if (row?.joiningDate != null) { dojRaw = row.joiningDate; break }
      }
    }
    if (dojRaw == null) dojRaw = policyData.DOJ

    const dojMs = parseDateOnlyToUtcMs(dojRaw)
    const effectiveMs = parseDateOnlyToUtcMs(policyEffectiveDate)
    const expiryMs = parseDateOnlyToUtcMs(policyExpiryDate)

    if (dojMs === null || effectiveMs === null || expiryMs === null) return 1
    // Only new joiners (DOJ strictly after policy start) are pro-rated
    if (dojMs <= effectiveMs) return 1

    const daysRemaining = daysBetweenInclusive(dojMs, expiryMs)
    const factor = daysRemaining / 365
    if (!Number.isFinite(factor)) return 1
    return Math.max(0, Math.min(1, factor))
  }

  const prorationFactor = getProrationFactor()
  const isProrated = prorationFactor < 1

  const [dependents, setDependents] = useState<Dependent[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingDependentId, setEditingDependentId] = useState<number | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [selectedFlexi, setSelectedFlexi] = useState<FlexiPlan | null>(null)
  const [selectedSumInsured, setSelectedSumInsured] = useState<number>(500000)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [enrolledMembers, setEnrolledMembers] = useState<EnrolledMember[]>([])
  const [loadingFrozen, setLoadingFrozen] = useState(false)
  const { currentUser } = useAuth()

  const [newDependent, setNewDependent] = useState<Dependent>({
    id: 0,
    name: '',
    dob: '',
    relation: '',
    gender: '',
  })

  // Max 2 parents (any combination of Parent and ParentInLaw)
  const maxTotalDependents = 2

  useEffect(() => {
    if (preloadedMembers && preloadedMembers.length > 0) {
      setEnrolledMembers(preloadedMembers)
      setLoadingFrozen(false)
    } else if (initialDependents && initialDependents.length > 0) {
      const mappedDependents = initialDependents
        .map((item, index) => {
          let relation = item.Relation
          const rLower = relation ? relation.toLowerCase() : ''

          if (['father', 'mother', 'parent'].includes(rLower)) relation = 'Parent'
          else if (rLower.includes('law')) relation = 'ParentInLaw'

          return {
            id: item.sid || Date.now() + index,
            name: item.Name,
            dob: item.DOB ? toISTInputFormat(item.DOB) : '',
            relation: relation,
            gender: item.Gender || '',
          }
        })
        .filter(
          (dep) => dep.relation === 'Self' || dep.relation === 'Parent' || dep.relation === 'ParentInLaw'
        )
      setDependents(mappedDependents)
    } else {
      // Initialize with Employee as first dependent (saved without coverage)
      setDependents([
        {
          id: 1,
          name: policyData.EmployeeName,
          dob: policyData.DOB ? toISTInputFormat(policyData.DOB) : '',
          relation: 'Self',
          gender: policyData.Gender || '',
        },
      ])
    }
  }, [policyData, preloadedMembers, initialDependents])

  const calculatePremium = () => {
    // Parents share the sum insured; premium is based on count (single/double), NOT age
    const familyDependents = dependents.filter((d) => d.relation !== 'Self')
    const dependentCount = familyDependents.length

    // Look up premium based on selected sum insured and parent count
    const premiumEntry = PREMIUM_TABLE[selectedSumInsured]
    let rawDependentPremium = 0
    if (premiumEntry && dependentCount > 0) {
      rawDependentPremium = dependentCount === 1 ? premiumEntry.single : premiumEntry.double
    }

    // Apply pro-rata to base parent premium (new joiners only; factor = 1 for full-year members)
    const dependentPremium = Math.round(rawDependentPremium * prorationFactor)

    // Apply pro-rata to flexi/top-up premium
    const rawFlexiPremium = selectedFlexi ? selectedFlexi.premium : 0
    const flexiPremium = Math.round(rawFlexiPremium * prorationFactor)

    const dependentGST = Math.round(dependentPremium * gstRate)
    const dependentTotal = dependentPremium + dependentGST

    const flexiGST = Math.round(flexiPremium * gstRate)
    const flexiTotal = flexiPremium + flexiGST

    const totalTaxable = dependentPremium + flexiPremium
    const totalGST = dependentGST + flexiGST
    const totalPayable = totalTaxable + totalGST

    return {
      dependentPremium,
      dependentCount,
      dependentGST,
      dependentTotal,
      flexiPremium,
      flexiGST,
      flexiTotal,
      gst: totalGST,
      total: totalPayable,
    }
  }

  const parentCount = dependents.filter((d) => d.relation !== 'Self').length
  const isMaxParentsReached = parentCount >= maxTotalDependents

  const handleAddClick = () => {
    setError(null)

    if (isMaxParentsReached) {
      return
    }

    setNewDependent({
      id: Date.now(),
      name: '',
      dob: '',
      relation: '',
      gender: '',
    })
    setShowAddModal(true)
  }

  const validateDependent = (dep: Dependent) => {
    setError(null)
    if (!dep.name || !dep.dob || !dep.relation || !dep.gender) {
      setError('All fields are required')
      return false
    }

    if (!RELATIONS.includes(dep.relation)) {
      setError('Only Parent or Parent-in-law relations are allowed.')
      return false
    }

    // Exclude the currently-editing dependent from counts
    const otherDeps = editingDependentId
      ? dependents.filter((d) => d.id !== editingDependentId)
      : dependents

    // Total parent count validation (max 2 across both groups, cross combination allowed)
    const existingParentCount = otherDeps.filter(
      (d) => d.relation === 'Parent' || d.relation === 'ParentInLaw'
    ).length

    if (existingParentCount >= maxTotalDependents) {
      setError('Maximum 2 parents allowed (any combination of Parent and Parent-in-law).')
      return false
    }

    return true
  }

  const saveDependent = () => {
    if (!validateDependent(newDependent)) return

    if (editingDependentId) {
      setDependents(dependents.map((d) => (d.id === editingDependentId ? newDependent : d)))
    } else {
      setDependents([...dependents, newDependent])
    }
    setEditingDependentId(null)
    setShowAddModal(false)
  }

  const handleEditClick = (dep: Dependent) => {
    setError(null)
    setEditingDependentId(dep.id)
    setNewDependent({ ...dep })
    setShowAddModal(true)
  }

  const removeDependent = (id: number) => {
    const updated = dependents.filter((d) => d.id !== id)
    setDependents(updated)
    setError(null)
    // Reset flexi selection if no parents remain
    if (updated.filter((d) => d.relation !== 'Self').length === 0) {
      setSelectedFlexi(null)
    }
  }

  const handleSubmit = async () => {
    if (isSubmitting) return

    setError(null)
    setIsSubmitting(true)

    const premiumData = calculatePremium()

    // Employee is saved without coverage, only parents share sum insured
    const familyMembersWithPremium = dependents.map((dep) => {
      const baseMember = {
        EmployeeNumber: currentUser?.EmployeeId,
        name: dep.name,
        dob: dep.dob,
        relation: dep.relation,
        gender: dep.gender,
        premium: dep.relation === 'Self' ? 0 : premiumData.dependentPremium,
        Grade: policyData.Grade,
        crm_client_id: policyCrmDbName || policyData.CorpCode,
        Email: currentUser?.Email,
        CorporateCode: policyData.CorporateCode,
        SumInsured: selectedSumInsured,
      }

      if (dep.relation === 'Self') {
        return {
          ...baseMember,
          premiumOpted: Boolean(selectedFlexi),
          additionalPremium: premiumData.total,
          livesCharged: premiumData.dependentCount,
          grade: policyData.Grade,

          // Flexi Benefit details (mapped as topup fields for same endpoint)
          TopupName: selectedFlexi?.name || '',
          topupsumInsured: selectedFlexi?.sumInsured || 0,
          topup_base_premium: premiumData.flexiPremium,
          topup_gst: premiumData.flexiGST,
          topup_total_premium: premiumData.flexiTotal,

          // Base (Dependent/Parent) details
          base_premium: premiumData.dependentPremium,
          base_gst: premiumData.dependentGST,
          base_total_premium: premiumData.dependentTotal,

          // Grand totals
          total_premium_payable: premiumData.total,
          total_gst: premiumData.gst,
        }
      }

      return baseMember
    })

    console.log('Submitting payload:', familyMembersWithPremium)

    try {
      const response = await fetch(
        'https://etl.futurisk.in/webhook/2dd87d62-8fd3-4cf6-a7e9-2ae88a23af4c',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(currentUser?.sessioncode ? { SessionCode: currentUser.sessioncode } : {}),
          },
          body: JSON.stringify(familyMembersWithPremium),
        }
      )

      if (response.ok) {
        setShowSuccess(true)
      } else {
        setError('Failed to submit enrollment. Please try again.')
        setIsSubmitting(false)
      }
    } catch (e) {
      console.error(e)
      setError('Network error occurred. Please try again.')
      setIsSubmitting(false)
    }
  }

  const premiumInfo = calculatePremium()

  // ─── Frozen / Read-only State ───────────────────────────────────────
  if (policyData.freezeEnrolment) {
    if (loadingFrozen && (!preloadedMembers || preloadedMembers.length === 0))
      return (
        <div className='text-center p-5 text-primary'>
          <span className='spinner-border spinner-border-sm align-middle ms-2'></span> Loading
          enrollment details...
        </div>
      )

    const membersToDisplay =
      preloadedMembers && preloadedMembers.length > 0 ? preloadedMembers : enrolledMembers

    const frozenPremium = {
      dependentPremium: membersToDisplay.reduce((acc, m) => acc + (m.BasePremium || 0), 0),
      flexiPremium: membersToDisplay.reduce((acc, m) => acc + (m.TopupBasePremium || 0), 0),
      gst: membersToDisplay.reduce((acc, m) => acc + (m.TotalGst || 0), 0),
      total: membersToDisplay.reduce((acc, m) => acc + (m.TotalPremiumPayable || 0), 0),
    }

    const optedFlexiMember = membersToDisplay.find((m) => m.TopupName)
    const optedFlexi = optedFlexiMember?.TopupName
    const optedFlexiSI = optedFlexiMember?.TopupSumInsured

    // Get the actual enrolled sum insured from member data
    const enrolledMember = membersToDisplay.find((m) => (m.BaseSumInsured && m.BaseSumInsured > 0) || (m.SumInsured && m.SumInsured > 0))
    const enrolledSumInsured = enrolledMember?.BaseSumInsured || enrolledMember?.SumInsured || selectedSumInsured

    return (
      <div className='card'>
        <div className='card-body'>
          <Row>
            <Col md={8}>
              <div className='d-flex justify-content-between align-items-center mb-4'>
                <h3 className='mb-0'>Enrolled Parents Details</h3>
                {enrolledSumInsured > 0 && (
                  <div className='badge badge-light-primary fs-6 p-3'>
                    Sum Insured: ₹{enrolledSumInsured.toLocaleString('en-IN')} (Parents share)
                  </div>
                )}
              </div>
              <div className='table-responsive mb-5'>
                <Table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bolder text-muted'>
                      <th>Name</th>
                      <th>Relation</th>
                      <th>Gender</th>
                      <th>Date of Birth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {membersToDisplay.map((member) => (
                      <tr key={member.Id}>
                        <td>
                          <div className='d-flex align-items-center'>
                            <div className='d-flex justify-content-start flex-column'>
                              <div className='text-dark fw-bolder fs-6'>{member.Name}</div>
                            </div>
                          </div>
                        </td>
                        <td>{member.Relation === 'ParentInLaw' ? 'Parent-in-law' : member.Relation}</td>
                        <td>{member.Gender}</td>
                        <td>{formatDateIST(member.Dob)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Selected Flexi Plan */}
              {flexiEnabled && <>
              <h3 className='mb-3'>Selected Flexi Benefits Plan</h3>
              <div className='alert alert-info d-flex align-items-center mb-3'>
                <i className='bi bi-info-circle-fill text-info fs-3 me-3'></i>
                <div className='text-gray-700 fw-bold fs-7'>
                  Flexi coverage provides additional sum insured over and above base parental policy for enrolled parents.
                </div>
              </div>
              <div className='p-4 bg-light rounded border border-gray-300'>
                {optedFlexi ? (
                  <div className='fw-bold fs-5 text-gray-800'>
                    {optedFlexi}{' '}
                    {optedFlexiSI
                      ? `- Sum Insured: ₹${optedFlexiSI.toLocaleString('en-IN')}`
                      : ''}
                  </div>
                ) : (
                  <div className='text-muted'>No Flexi Benefits plan selected.</div>
                )}
              </div>
              </>}
            </Col>
            <Col md={4}>
              <div className='card bg-light p-4 sticky-top' style={{ top: '20px' }}>
                <h4 className='mb-4'>Premium Summary</h4>

                {frozenPremium.dependentPremium > 0 && (
                  <div className='mb-3'>
                    <strong>Parents Premium:</strong>
                    <div className='float-end'>
                      ₹{frozenPremium.dependentPremium.toLocaleString('en-IN')}
                    </div>
                  </div>
                )}

                {flexiEnabled && frozenPremium.flexiPremium > 0 && (
                  <div className='mb-3'>
                    <strong>Flexi Benefits Premium:</strong>
                    <div className='float-end'>
                      ₹{frozenPremium.flexiPremium.toLocaleString('en-IN')}
                    </div>
                  </div>
                )}

                {frozenPremium.total > 0 ? (
                  <>
                    <hr />
                    <div className='mb-2'>
                      <span>Total Amount:</span>
                      <span className='float-end'>
                        ₹{(frozenPremium.dependentPremium + frozenPremium.flexiPremium).toLocaleString('en-IN')}
                      </span>
                    </div>
                    {gstRate > 0 && (
                      <div className='mb-2'>
                        <span>GST ({(gstRate * 100).toFixed(0)}%):</span>
                        <span className='float-end'>₹{frozenPremium.gst.toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    <hr />
                    <h5 className='text-primary'>
                      Total Payable:
                      <span className='float-end'>₹{frozenPremium.total.toLocaleString('en-IN')}</span>
                    </h5>
                    <div className='text-muted fs-7 mt-2 text-end'>
                      The total premium will be deducted from your next salary.
                    </div>
                  </>
                ) : (
                  <div className='text-muted text-center py-5'>
                    No additional premium applicable.
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </div>
    )
  }

  // ─── Success State ──────────────────────────────────────────────────
  if (showSuccess) {
    return (
      <div className='card'>
        <div className='card-body text-center py-5'>
          <div className='mb-4'>
            <KTSVG path='/media/icons/duotune/general/gen043.svg' className='svg-icon-3x svg-icon-success' />
          </div>
          <h2 className='text-success mb-4'>Thank You!</h2>

          {premiumInfo.total > 0 ? (
            <div className='alert alert-info d-inline-block p-4'>
              <span className='fs-4'>
                An amount of <strong>₹{premiumInfo.total.toLocaleString('en-IN')}</strong> will be
                deducted from your salary.
              </span>
            </div>
          ) : (
            <div className='fs-4 text-gray-600'>Your enrollment is complete.</div>
          )}
        </div>
      </div>
    )
  }

  // ─── Active Enrollment State ────────────────────────────────────────
  return (
    <div className='card'>
      <div className='card-body'>
        {error && !showAddModal && (
          <div className='mb-4 text-danger fw-bold border border-danger p-3 rounded'>{error}</div>
        )}
        <Row>
          <Col md={8}>
            <div className='d-flex justify-content-between align-items-center mb-4'>
              <div>
                <h3 className='mb-0'>Parental Policy - Family Details</h3>
                <div className='text-muted fs-7 mt-1'>
                  Coverage: Up to 2 Parents / Parents-in-law (cross combination allowed). Parents share the sum insured.
                </div>
              </div>
              <Button
                variant={isMaxParentsReached ? 'secondary' : 'primary'}
                onClick={handleAddClick}
                disabled={isMaxParentsReached}
                title={isMaxParentsReached ? `Maximum ${maxTotalDependents} parents already added` : 'Add a parent'}
              >
                {isMaxParentsReached ? 'Max Parents Added' : 'Add Parent'}
              </Button>
            </div>

            <div className='table-responsive mb-5'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bolder'>
                    <th className='min-w-150px'>Name</th>
                    <th className='min-w-100px'>Relation</th>
                    <th className='min-w-100px'>Gender</th>
                    <th className='min-w-100px'>DOB</th>
                    <th className='min-w-100px'>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dependents.map((dep) => (
                    <tr key={dep.id} className='text-capitalize text-dark'>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='d-flex justify-content-start flex-column'>
                            <span className='fw-bolder text-hover-primary fs-6 text-dark'>
                              {dep.name}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                          {dep.relation === 'ParentInLaw' ? 'Parent-in-law' : dep.relation}
                        </span>
                      </td>
                      <td>
                        <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                          {dep.gender}
                        </span>
                      </td>
                      <td>
                        <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                          {formatDateIST(dep.dob)}
                        </span>
                      </td>
                      <td>
                        {dep.relation !== 'Self' && (
                          <div className='d-flex gap-2'>
                            <Button
                              variant='primary'
                              size='sm'
                              onClick={() => handleEditClick(dep)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant='danger'
                              size='sm'
                              onClick={() => removeDependent(dep.id)}
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Sum Insured Selection - below family table */}
            <div className='mb-5'>
              <h5 className='mb-3'>Select Sum Insured</h5>
              <div className='d-flex gap-3'>
                {SUM_INSURED_OPTIONS.map((opt) => (
                  <div
                    key={opt.value}
                    className={`border rounded p-3 flex-fill text-center cursor-pointer ${
                      selectedSumInsured === opt.value
                        ? 'border-primary bg-light-primary'
                        : 'border-gray-300 bg-white'
                    }`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedSumInsured(opt.value)}
                  >
                    <Form.Check
                      type='radio'
                      label={
                        <span className='fw-bold fs-6'>
                          {opt.label} (₹{opt.value.toLocaleString('en-IN')})
                        </span>
                      }
                      name='sumInsured'
                      id={`si-${opt.value}`}
                      checked={selectedSumInsured === opt.value}
                      onChange={() => setSelectedSumInsured(opt.value)}
                    />
                    {PREMIUM_TABLE[opt.value] && (
                      <div className='text-muted fs-8 mt-1'>
                        Single: ₹{PREMIUM_TABLE[opt.value].single.toLocaleString('en-IN')} | Double: ₹{PREMIUM_TABLE[opt.value].double.toLocaleString('en-IN')}
                        <br /><small>{gstRate > 0 ? '(Excl. GST@' + (gstRate * 100).toFixed(0) + '%)' : ''}</small>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ─── Flexi Benefits Section (only visible when at least one parent is added) ─────────────────────────── */}
            {flexiEnabled && parentCount === 0 && (
              <div className='alert alert-light-warning border border-warning d-flex align-items-center mb-5'>
                <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-2x svg-icon-warning me-3' />
                <div className='text-gray-700 fw-bold fs-7'>
                  Please add at least one parent to view and select Flexi Benefits Plans.
                </div>
              </div>
            )}
            {flexiEnabled && parentCount > 0 && <div className='mb-5'>
              <h3 className='mb-3'>
                <KTSVG path='/media/icons/duotune/general/gen049.svg' className='svg-icon-2 svg-icon-warning me-2' />
                Flexi Benefits Plans
              </h3>

              <div className='alert alert-info d-flex align-items-center mb-4'>
                <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-2x svg-icon-info me-3' />
                <div className='text-gray-700 fw-bold fs-7'>
                  Flexi Benefits provide additional sum insured with enhanced coverage over and
                  above your base parental policy. Choose one plan that best suits your family's needs. The
                  premium {gstRate > 0 ? '(excluding GST@' + (gstRate * 100).toFixed(0) + '%)' : ''} will be deducted from your salary.
                </div>
              </div>

              {/* Coverage Comparison Table */}
              <div className='table-responsive mb-4'>
                <table className='table table-bordered table-hover table-sm align-middle'>
                  <thead>
                    <tr className='bg-primary text-white text-center'>
                      <th
                        className='text-start ps-3 align-middle'
                        style={{ minWidth: '180px', position: 'sticky', left: 0, background: '#009ef7', zIndex: 1 }}
                      >
                        Coverage
                      </th>
                      {PLAN_NAMES.map((plan, idx) => (
                        <th
                          key={idx}
                          className={`align-middle ${
                            selectedFlexi?.name === plan
                              ? 'bg-success text-white'
                              : ''
                          }`}
                          style={{ minWidth: '120px', cursor: 'pointer' }}
                          onClick={() => {
                            const fp = FLEXI_PLANS.find((f) => f.name === plan)
                            if (fp) setSelectedFlexi(selectedFlexi?.name === plan ? null : fp)
                          }}
                        >
                          <div className='fw-bolder fs-7'>{plan}</div>
                          {selectedFlexi?.name === plan && (
                            <KTSVG path='/media/icons/duotune/general/gen043.svg' className='svg-icon-2 svg-icon-white' />
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {COVERAGE_DATA.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        <td
                          className='fw-bold text-gray-800 ps-3 fs-8'
                          style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}
                        >
                          {row.label}
                        </td>
                        {row.values.map((val, colIdx) => (
                          <td
                            key={colIdx}
                            className={`text-center fs-8 ${
                              selectedFlexi?.name === PLAN_NAMES[colIdx]
                                ? 'bg-light-success fw-bold'
                                : ''
                            } ${val === 'NA' ? 'text-muted' : 'text-dark'}`}
                          >
                            {val}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {/* Premium Row */}
                    <tr className='bg-light-warning'>
                      <td
                        className='fw-bolder text-gray-900 ps-3 fs-7'
                        style={{ position: 'sticky', left: 0, background: '#fff3cd', zIndex: 1 }}
                      >
                        FLEXI Premium
                        <br />
                        <small className='text-muted'>(Per Family{gstRate > 0 ? ', Excl. GST@' + (gstRate * 100).toFixed(0) + '%' : ''})</small>
                      </td>
                      {PLAN_PREMIUMS.map((price, idx) => (
                        <td
                          key={idx}
                          className={`text-center fw-bolder fs-7 ${
                            selectedFlexi?.name === PLAN_NAMES[idx]
                              ? 'bg-success text-white'
                              : 'text-primary'
                          }`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            const fp = FLEXI_PLANS[idx]
                            setSelectedFlexi(selectedFlexi?.name === fp.name ? null : fp)
                          }}
                        >
                          {price}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Radio Selection */}
              <div className='border rounded p-4 bg-light-primary'>
                <div className='fw-bold fs-6 mb-3 text-gray-800'>Select Your Plan:</div>
                <Form.Group>
                  <Form.Check
                    type='radio'
                    label={
                      <span className='fw-bold text-gray-700'>
                        No Flexi Benefits
                      </span>
                    }
                    name='flexiPlan'
                    id='flexi-none'
                    checked={selectedFlexi === null}
                    onChange={() => setSelectedFlexi(null)}
                    className='mb-3 p-2 ps-6 rounded border bg-white'
                  />
                  {FLEXI_PLANS.map((plan, idx) => (
                    <Form.Check
                      key={idx}
                      type='radio'
                      label={
                        <span className='fw-bold'>
                          <span className={`badge ${selectedFlexi?.name === plan.name ? 'badge-success' : 'badge-light-primary'} me-2`}>
                            {plan.name}
                          </span>
                          Sum Insured: ₹{plan.sumInsured.toLocaleString('en-IN')} &mdash; Premium: ₹
                          {plan.premium.toLocaleString('en-IN')}
                          {gstRate > 0 && <span className='text-muted fs-8'> + GST</span>}
                        </span>
                      }
                      name='flexiPlan'
                      id={`flexi-${idx}`}
                      checked={selectedFlexi?.name === plan.name}
                      onChange={() => setSelectedFlexi(plan)}
                      className={`mb-2 p-2 ps-6 rounded border ${
                        selectedFlexi?.name === plan.name
                          ? 'bg-light-success border-success'
                          : 'bg-white'
                      }`}
                    />
                  ))}
                </Form.Group>
              </div>
            </div>}
          </Col>

          {/* ─── Premium Summary Sidebar ─────────────────────────── */}
          <Col md={4}>
            <div className='card bg-light p-4 sticky-top' style={{ top: '20px' }}>
              <h4 className='mb-4'>Premium Summary</h4>

              <div className='mb-3 p-3 bg-white rounded border'>
                <div className='text-muted fs-7'>Selected Sum Insured</div>
                <div className='fw-bolder fs-4 text-primary'>
                  ₹{selectedSumInsured.toLocaleString('en-IN')}
                </div>
                <div className='text-muted fs-8 mt-1'>Parents share this sum insured</div>
              </div>

              {premiumInfo.dependentCount > 0 && (
                <div className='mb-3 d-flex justify-content-between'>
                  <strong>Parents Premium ({premiumInfo.dependentCount === 1 ? 'Single' : 'Double'}):</strong>
                  <span className='fw-bold'>₹{premiumInfo.dependentPremium.toLocaleString('en-IN')}</span>
                </div>
              )}

              {flexiEnabled && selectedFlexi && (
                <div className='mb-3 d-flex justify-content-between'>
                  <strong>Flexi Benefits ({selectedFlexi.name}):</strong>
                  <span className='fw-bold'>₹{premiumInfo.flexiPremium.toLocaleString('en-IN')}</span>
                </div>
              )}

              {isProrated && (premiumInfo.dependentCount > 0 || (flexiEnabled && selectedFlexi)) && (
                <div className='alert alert-warning py-2 px-3 mb-3 d-flex align-items-center gap-2'>
                  <span className='badge badge-warning me-2'>Pro-rated</span>
                  <span className='text-dark fs-7'>
                    Premium calculated for <strong>{Math.round(prorationFactor * 365)} days</strong> remaining in the policy year (factor: {(prorationFactor * 100).toFixed(1)}%).
                  </span>
                </div>
              )}

              {(premiumInfo.dependentCount > 0 || (flexiEnabled && selectedFlexi)) ? (
                <>
                  <hr />
                  {premiumInfo.dependentCount > 0 && (
                    <div className='mb-2 d-flex justify-content-between'>
                      <span>Parents Base Premium:</span>
                      <span>₹{premiumInfo.dependentPremium.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  {flexiEnabled && selectedFlexi && (
                    <div className='mb-2 d-flex justify-content-between'>
                      <span>Flexi Premium:</span>
                      <span>₹{premiumInfo.flexiPremium.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  <div className='mb-2 d-flex justify-content-between'>
                    <span>Total Amount:</span>
                    <span>
                      ₹{(premiumInfo.dependentPremium + premiumInfo.flexiPremium).toLocaleString('en-IN')}
                    </span>
                  </div>
                  {gstRate > 0 && (
                    <div className='mb-2 d-flex justify-content-between'>
                      <span>GST ({(gstRate * 100).toFixed(0)}%):</span>
                      <span>₹{premiumInfo.gst.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  <hr />
                  <h5 className='text-primary d-flex justify-content-between'>
                    <span>Total Payable:</span>
                    <span>₹{premiumInfo.total.toLocaleString('en-IN')}</span>
                  </h5>
                  <div className='text-muted fs-7 mt-2 text-end'>
                    This amount will be deducted from your salary.
                  </div>
                </>
              ) : (
                <div className='text-muted text-center py-5'>
                  No additional premium applicable.
                  <br />
                  <small>Add parents{flexiEnabled ? ' and/or select a Flexi Benefits plan' : ''} to see premium.</small>
                </div>
              )}

              <div className='d-grid gap-2 mt-4'>
                <Button
                  variant='success'
                  size='lg'
                  onClick={() => setShowConfirmModal(true)}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className='spinner-border spinner-border-sm me-2'
                        role='status'
                        aria-hidden='true'
                      ></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Enrollment'
                  )}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Add Parent Modal */}
      <Modal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false)
          setEditingDependentId(null)
          setError(null)
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{editingDependentId ? 'Edit Parent' : 'Add Parent'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <div className='mb-4 text-danger fw-bold border border-danger p-3 rounded'>
              {error}
            </div>
          )}
          <div className='alert alert-light-info border border-info p-3 mb-4'>
            <div className='fs-8 text-gray-700'>
              <strong>Allowed Relations:</strong> Parent, Parent-in-law (any 2 from both groups, cross combination allowed)
            </div>
          </div>
          <Form>
            <Form.Group className='mb-3'>
              <Form.Label>Name</Form.Label>
              <Form.Control
                type='text'
                value={newDependent.name}
                onChange={(e) => setNewDependent({ ...newDependent, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Relation</Form.Label>
              <Form.Select
                value={newDependent.relation}
                onChange={(e) => setNewDependent({ ...newDependent, relation: e.target.value })}
              >
                <option value=''>Select Relation</option>
                {RELATIONS.map((r) => (
                  <option key={r} value={r}>
                    {r === 'ParentInLaw' ? 'Parent-in-law' : r}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Gender</Form.Label>
              <Form.Select
                value={newDependent.gender}
                onChange={(e) => setNewDependent({ ...newDependent, gender: e.target.value })}
              >
                <option value=''>Select Gender</option>
                {GENDERS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control
                type='date'
                value={newDependent.dob}
                onChange={(e) => setNewDependent({ ...newDependent, dob: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => { setShowAddModal(false); setEditingDependentId(null) }}>
            Cancel
          </Button>
          <Button variant='primary' onClick={saveDependent}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Final Confirmation Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        backdrop='static'
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Enrollment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='alert alert-warning d-flex align-items-center'>
            <span className='svg-icon svg-icon-2hx svg-icon-warning me-4'>
              <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-2x svg-icon-warning' />
            </span>
            <div className='d-flex flex-column'>
              {premiumInfo.total > 0 && (
                <>
                  <div className='fw-bold mb-1'>
                    Total Premium Deduction: ₹{premiumInfo.total.toLocaleString('en-IN')}
                  </div>
                  <div className='small'>This amount will be debited from your salary.</div>
                </>
              )}
              <div className='small mt-1'>
                <strong>
                  Please review and confirm your nominations carefully. Once submitted, no changes
                  can be made in the policy year.
                </strong>
              </div>
            </div>
          </div>
          <p className='mb-0 mt-3'>Do you want to proceed to enroll your parents?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowConfirmModal(false)}>
            Go Back
          </Button>
          <Button
            variant='primary'
            onClick={() => {
              setShowConfirmModal(false)
              handleSubmit()
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                ></span>
                Submitting...
              </>
            ) : (
              'Yes, Enroll My Parents'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
